import { useCallback, useMemo, useState, useEffect } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { ReactComponent as CopyIcon } from "@root/assets/icons/copy-icon-new.svg";
import { ReactComponent as LikeIcon } from "@root/assets/icons/like-icon-new.svg";
import { ReactComponent as DislikeIcon } from "@root/assets/icons/dislike-icon-new.svg";
import { ReactComponent as EditIcon } from "@root/assets/icons/edit-icon-new.svg";
import { ReactComponent as SettingsIcon } from "@root/assets/icons/settings-icon-new.svg";
import { useCopyAnswer } from "@root/hooks/useCopyAnswer";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { CHAT_ACTIONS, CHAT_LIKE_STATUS } from "@root/constants";

type ActionKey = CHAT_ACTIONS;

const iconMap: Partial<
  Record<ActionKey, React.ComponentType<{ color?: string }>>
> = {
  [CHAT_ACTIONS.COPY]: CopyIcon,
  [CHAT_ACTIONS.LIKE]: LikeIcon,
  [CHAT_ACTIONS.DISLIKE]: DislikeIcon,
  // [CHAT_ACTIONS.EDIT]: EditIcon,
  // [CHAT_ACTIONS.SETTINGS]: SettingsIcon,
};

const actionOrder: ActionKey[] = [
  CHAT_ACTIONS.COPY,
  CHAT_ACTIONS.LIKE,
  CHAT_ACTIONS.DISLIKE,
  // CHAT_ACTIONS.EDIT,
  // CHAT_ACTIONS.SETTINGS,
];

interface SystemChatActionsProps {
  answer?: string | string[] | null;
  chat: any;
  onRefetch?: () => void;
}

function SystemChatActions({
  answer,
  chat,
  onRefetch,
}: SystemChatActionsProps) {
  const { handleCopy, isCopyDisabled } = useCopyAnswer(answer);
  const { currentMessageResponse, likeChat } = useMainChatContext();

  const chatId = useMemo(() => {
    return (
      chat?.meta?.c?.id ||
      chat?.meta?.id ||
      chat?.chatId ||
      chat?.id ||
      currentMessageResponse?.chatId ||
      currentMessageResponse?.id
    );
  }, [chat, currentMessageResponse]);

  const initialLikeStatus = useMemo(() => {
    return (
      chat?.meta?.c?.is_liked ??
      chat?.meta?.is_liked ??
      chat?.is_liked ??
      currentMessageResponse?.is_liked ??
      CHAT_LIKE_STATUS.NONE
    );
  }, [chat, currentMessageResponse]);

  const [likeStatus, setLikeStatus] = useState(initialLikeStatus);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setLikeStatus(initialLikeStatus);
  }, [initialLikeStatus]);

  const handleLike = useCallback(async () => {
    if (!chatId || isLoading) return;

    const previousStatus = likeStatus;
    const newStatus =
      likeStatus === CHAT_LIKE_STATUS.LIKED
        ? CHAT_LIKE_STATUS.NONE
        : CHAT_LIKE_STATUS.LIKED;

    setLikeStatus(newStatus);
    setIsLoading(true);

    try {
      await likeChat({ chatId, is_liked: newStatus });
      onRefetch?.();
    } catch {
      setLikeStatus(previousStatus);
    } finally {
      setIsLoading(false);
    }
  }, [chatId, likeChat, likeStatus, isLoading, onRefetch]);

  const handleDislike = useCallback(async () => {
    if (!chatId || isLoading) return;

    const previousStatus = likeStatus;
    const newStatus =
      likeStatus === CHAT_LIKE_STATUS.DISLIKED
        ? CHAT_LIKE_STATUS.NONE
        : CHAT_LIKE_STATUS.DISLIKED;

    setLikeStatus(newStatus);
    setIsLoading(true);

    try {
      await likeChat({ chatId, is_liked: newStatus });
      onRefetch?.();
    } catch {
      setLikeStatus(previousStatus);
    } finally {
      setIsLoading(false);
    }
  }, [chatId, likeChat, likeStatus, isLoading, onRefetch]);

  const actionHandlers: Record<ActionKey, () => void> = useMemo(
    () => ({
      [CHAT_ACTIONS.COPY]: handleCopy,
      [CHAT_ACTIONS.LIKE]: handleLike,
      [CHAT_ACTIONS.DISLIKE]: handleDislike,
      [CHAT_ACTIONS.EDIT]: () => {},
      [CHAT_ACTIONS.SETTINGS]: () => {},
    }),
    [handleCopy, handleLike, handleDislike]
  );

  const getIconColor = (key: ActionKey) => {
    if (key === CHAT_ACTIONS.LIKE && likeStatus === CHAT_LIKE_STATUS.LIKED)
      return "#22C55E";
    if (
      key === CHAT_ACTIONS.DISLIKE &&
      likeStatus === CHAT_LIKE_STATUS.DISLIKED
    )
      return "#EF4444";
    return "#6B7280";
  };

  const getBackgroundColor = (key: ActionKey) => {
    if (key === CHAT_ACTIONS.LIKE && likeStatus === CHAT_LIKE_STATUS.LIKED)
      return "#DCFCE7";
    if (
      key === CHAT_ACTIONS.DISLIKE &&
      likeStatus === CHAT_LIKE_STATUS.DISLIKED
    )
      return "#FEE2E2";
    return "transparent";
  };

  const isLikeDisabled = !chatId;

  return (
    <Flex align="center" gap="10px">
      {actionOrder.map((key) => {
        const Icon = iconMap[key];
        if (!Icon) return null; // Skip if icon is not defined (commented out actions)
        const isCopyAction = key === CHAT_ACTIONS.COPY;
        const isLikeAction =
          key === CHAT_ACTIONS.LIKE || key === CHAT_ACTIONS.DISLIKE;

        const isDisabled =
          (isCopyAction && isCopyDisabled) ||
          (isLikeAction && isLikeDisabled) ||
          (isLikeAction && isLoading);

        return (
          <Box
            key={key}
            width="28px"
            height="28px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            cursor={isDisabled ? "not-allowed" : "pointer"}
            opacity={isDisabled ? 0.4 : 1}
            bg={getBackgroundColor(key)}
            onClick={() => !isDisabled && actionHandlers[key]()}
          >
            <Icon color={getIconColor(key)} />
          </Box>
        );
      })}
    </Flex>
  );
}

export default SystemChatActions;
